import axios, { AxiosResponse } from "axios";
import { saveAs } from "file-saver";
import { pdApiUrl } from "../../utils/pdApi";

/**
 * A single rejected row returned by the backend `ValidationResult`.
 *
 * Property names mirror the C# `RejectedRow` record serialized by ASP.NET
 * (default camelCase JSON): `RowPosition` -> `rowPosition`, `Reasons` -> `reasons`.
 */
export type BulkUploadRejectedRow = {
  /** 1-based position of the rejected row in the upload file. */
  rowPosition: number;
  /** One or more reasons, each naming the offending column and the violated rule. */
  reasons: string[];
};

/**
 * The validation outcome returned by `POST /api/faults/bulk-upload/{domainGroup}`.
 *
 * Mirrors the C# `ValidationResult` record (default camelCase JSON).
 */
export type BulkUploadValidationResult = {
  /** True only when every row was valid AND the rows were persisted. */
  inserted: boolean;
  /** Count of inserted fault rows (0 when any row was rejected). */
  insertedCount: number;
  /** Count of rejected rows. */
  rejectedCount: number;
  /** The rejected rows, each with its position and reasons. */
  rejectedRows: BulkUploadRejectedRow[];
};

/** Resolves the bulk-upload base path for a domain group. */
const bulkUploadBase = (domainGroup: Api.GroupName): string =>
  `${pdApiUrl}/faults/bulk-upload/${domainGroup.toLowerCase()}`;

/** Maximum upload size accepted by the client before a request is made (mirrors the API's 5 MB cap). */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * Extracts a human-readable message from a failed upload response. The backend returns a
 * `FileAcceptanceError` (`{ kind, missingColumns, message }`) with a 400 for unaccepted files, so
 * prefer its `message`. Falls back to a generic sentence when no structured message is present.
 */
export const extractUploadErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as { message?: string } | undefined;

    // Prefer the backend's structured, specific message (wrong file, empty, too large,
    // missing columns) whenever it is present.
    if (data && typeof data.message === "string" && data.message.trim().length > 0) {
      return data.message;
    }

    // A 5xx has no structured body: it is an unexpected server/persistence error, not a
    // template problem, so say so plainly instead of blaming the user's file.
    if (typeof status === "number" && status >= 500) {
      return (
        "The server could not process the upload due to an unexpected error. No faults were " +
        "inserted. Please try again, and contact support if the problem persists."
      );
    }

    // Any other response without a message: likely a network/authorization issue.
    if (typeof status === "number") {
      return `The upload could not be completed (error ${status}). No faults were inserted. ` +
        "Please try again.";
    }
  }
  return fallback;
};

/**
 * Downloads the Excel (.xlsx) template for the given domain group and triggers a
 * browser download. Mirrors the existing blob-download pattern (axios
 * `responseType: "blob"` + `file-saver`).
 */
export const downloadTemplate = async (domainGroup: Api.GroupName): Promise<void> => {
  const response: AxiosResponse<Blob> = await axios({
    url: `${bulkUploadBase(domainGroup)}/template`,
    method: "GET",
    responseType: "blob"
  });
  saveAs(response.data, `${domainGroup.toLowerCase()}-fault-template.xlsx`);
};

/**
 * Uploads the chosen Excel file to the bulk-upload endpoint for the given domain
 * group and returns the parsed `ValidationResult`. The file is sent as multipart
 * form data under the `file` field, matching the controller's `IFormFile file`
 * parameter.
 */
export const uploadFaults = async (
  domainGroup: Api.GroupName,
  file: File
): Promise<BulkUploadValidationResult> => {
  const formData = new FormData();
  formData.append("file", file);

  const response: AxiosResponse<BulkUploadValidationResult> = await axios.post(
    bulkUploadBase(domainGroup),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }
  );

  return response.data;
};
